/**
 * Optimized Feature Cards Component
 * Performance improvements:
 * - React.memo on all components
 * - useMemo for static configs
 * - useCallback for event handlers
 * - Lazy loading for images
 * - Progressive disclosure optimization
 */

'use client';

import { memo, useMemo, useCallback, useRef, useEffect } from 'react';
import { featuresConfig } from '@/constant/home';
import { useIntersectionObserver, useAnalytics } from '@/hooks/usePerformance';
import Image from 'next/image';

type FeatureCardProps = {
  img: {
    src: string;
    alt: string;
    className?: string;
  };
  title: string;
  description: string;
  link: {
    href: string;
    text: string;
    srText?: string;
  };
  index: number;
  isVisible?: boolean;
};

// Memoized Feature Card with lazy loading
const FeatureCard = memo(function FeatureCard({
  img,
  title,
  description,
  link,
  index,
  isVisible = true,
}: FeatureCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);
  const isInView = useIntersectionObserver(cardRef, {
    threshold: 0.1,
    rootMargin: '50px',
  });
  const { trackEvent } = useAnalytics();

  // Track when card becomes visible (only once)
  useEffect(() => {
    if (isInView && isVisible && !hasTrackedView.current) {
      trackEvent('feature_card_view', {
        feature_name: title,
        feature_index: index,
      });
      hasTrackedView.current = true;
    }
  }, [isInView, isVisible, title, index, trackEvent]);

  // Track click events on card
  const handleCardClick = useCallback(() => {
    trackEvent('feature_card_click', {
      feature_name: title,
      feature_index: index,
      link_href: link.href,
    });
  }, [title, index, link.href, trackEvent]);

  // Optimize style object
  const cardStyle = useMemo(
    () => ({
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.3s ease, transform 0.3s ease',
    }),
    [isVisible]
  );

  return (
    <div
      ref={cardRef}
      className="feature-card_item border"
      style={cardStyle}
      data-feature-index={index}
      onClick={handleCardClick}
      role="article"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <div className="feature-card_image-wrapper">
        {/* Lazy load images only when in viewport */}
        {isInView ? (
          <Image
            src={img.src}
            alt={img.alt}
            width={400}
            height={300}
            className={img.className}
            loading="lazy"
            placeholder="blur"
            blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VlZSIvPjwvc3ZnPg=="
          />
        ) : (
          <div className="placeholder-shimmer" style={{ height: '300px' }} />
        )}
      </div>
      <div className="feature-card_content-wrapper">
        <div className="feature-card_content">
          <h3
            className="heading-xxsmall"
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <div className="padding-bottom padding-xxsmall" />
          <p
            className="text-gray-800 dark:text-gray-400"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      </div>
      {/* {link && (
        <div className="feature-card_link-wrapper">
          <a
            href={link.href}
            className="feature-card_link"
            onClick={handleClick}
            aria-label={link.srText || link.text}
          >
            {link.text}
          </a>
        </div>
      )} */}
    </div>
  );
});

// Memoized header component
const FeaturesHeader = memo(function FeaturesHeader({
  headline,
  subheading,
  headlineCss,
}: {
  headline: string;
  subheading: string;
  headlineCss: string;
}) {
  return (
    <div className="text-align-center">
      <h2 className={headlineCss}>{headline}</h2>
      <div className="padding-bottom padding-small" />
      <div className="max-width-large align-center">
        <p
          className="text-size-medium"
          dangerouslySetInnerHTML={{ __html: subheading }}
        />
      </div>
    </div>
  );
});


// Main optimized features component
export function HomeFeatures() {
  // Memoize config to prevent re-creation
  const config = useMemo(() => featuresConfig, []);

  // Memoize all features
  const allFeatures = useMemo(() => config.featureList, [config.featureList]);

  // Static grid styles for all features
  const gridStyles = useMemo(() => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridTemplateRows: 'auto auto',
    gridAutoColumns: '1fr',
    gridAutoFlow: 'row',
    gridColumnGap: '2rem',
    gridRowGap: '2rem',
  }), []);

  return (
    <div className="section_home-features">
      <div className="padding-vertical padding-xxsmall">
        <FeaturesHeader
          headline={config.headline}
          subheading={config.subheading}
          headlineCss={config.headlineCss}
        />
        <div className="padding-bottom padding-xlarge" />
        <div
          className="w-layout-grid feature-card_list"
          style={gridStyles}
        >
          {/* Show all features */}
          {allFeatures.map((feature, idx) => (
            <FeatureCard
              key={`feature-${idx}`}
              {...feature}
              index={idx}
              isVisible
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Export memoized component
export default memo(HomeFeatures);
